import { APP_INITIALIZER, Provider } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import de from '@angular/common/locales/de';
import extraDe from '@angular/common/locales/extra/de';
import dayjs from 'dayjs';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import localeData from 'dayjs/plugin/localeData'
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { LanguageStore } from '../stores/LanguageStore';
import * as deConfig from '../configs/languages/de';
import * as deDefaultSnippetsConfig from '../configs/snippets/de.json';
import { AuthService } from '../../features/auth/core/services/AuthService';
import { IdentityStore } from '../../features/auth/core/stores/IdentityStore';
import { Observable, tap } from 'rxjs';
import { IResponse } from '../interfaces/IResponse';
import { ILanguage } from '../interfaces/ILanguage';
import { environment } from '../../../environments/environment';
import { ISnippet } from '../interfaces/ISnippet';
import { ToolsService } from '../services/ToolsService';
import { SnippetsStore } from '../stores/SnippetsStore';
import * as Sentry from "@sentry/angular-ivy";

export const provideAppInitializer = (): Provider => {
  return {
    provide: APP_INITIALIZER,
    useFactory: (
      languageStore: LanguageStore,
      authService: AuthService,
      identityStore: IdentityStore,
      toolsService: ToolsService,
      snippetsStore: SnippetsStore,
    ) => {
      return () => {
        return new Promise<void>((resolve) => {
          const lang = location.pathname.split('/')[1];

          const getAllLanguages$ = (): Observable<IResponse<ILanguage[]>> => {
            const { fallbackLanguage } = environment;

            return toolsService.getAllLanguages().pipe(
              tap(languages => {
                let fLanguage = languages.data.find(x => x.key === lang);

                if (!fLanguage && lang !== fallbackLanguage) {
                  fLanguage = languageStore.language() ?? languages.data.find(x => x.key === fallbackLanguage);
                  location.href = `/${fLanguage.key}`;
                }

                languageStore.language.set(fLanguage);
                document.documentElement.lang = fLanguage.key;
                languageStore.languages.set(languages.data);
                import('../extensions/number.extensions').then();
                import('../extensions/string.extensions').then();
              })
            );
          };

          const getAllSnippets$ = (): Observable<IResponse<ISnippet[]>> => {
            return toolsService.getAllSnippets(languageStore.language().key).pipe(
              tap(snippets => snippetsStore.snippets.set(snippets.data))
            );
          };

          const updateDayjsLocales = (): void => {
            dayjs.locale(lang);
            dayjs.extend(isLeapYear);
            dayjs.extend(isoWeek);
            dayjs.extend(isoWeeksInYear);
            dayjs.extend(localeData);
            dayjs.extend(customParseFormat);
            dayjs.extend(advancedFormat);
            dayjs.extend(localizedFormat);
          };

          getAllLanguages$().subscribe(languages => {
            if (languageStore.language()) {
              getAllSnippets$().subscribe(snippets => {
                Promise.all([
                  import(
                    /* webpackExclude: /\.d\.ts$/ */
                    /* webpackMode: "lazy-once" */
                    /* webpackChunkName: "i18n" */
                    /* @vite-ignore */

                    `/node_modules/@angular/common/locales/${lang}`
                    ),
                  import(
                    /* webpackExclude: /\.d\.ts$/ */
                    /* webpackMode: "lazy-once" */
                    /* webpackChunkName: "i18n-extra" */
                    /* @vite-ignore */

                    `/node_modules/@angular/common/locales/extra/${lang}`
                    ),
                  import(
                    /* webpackExclude: /\.d\.ts$/ */
                    /* webpackMode: "lazy-once" */
                    /* webpackChunkName: "dayjs" */
                    /* @vite-ignore */

                    `/node_modules/dayjs/locale/${lang}`
                    ),
                  import(
                    `/src/app/core/configs/languages/${lang}`
                    ),
                  import(
                    `/src/app/core/configs/snippets/${lang}.json`
                    )
                ])
                  .then(([module1, module2, module3, module4, module5]) => {
                    snippetsStore.defaultSnippets.set(module5.default);
                    languageStore.languageConfig.set(module4.default);
                    registerLocaleData(module1.default, 'main', module2.default);
                    updateDayjsLocales();
                  })
                  .catch(error => {
                    snippetsStore.defaultSnippets.set(deDefaultSnippetsConfig['default']);
                    languageStore.languageConfig.set(deConfig.default);
                    registerLocaleData(de, 'main', extraDe);
                    updateDayjsLocales();
                  })
                  .finally(() => {
                    if (identityStore.identity()) {
                      authService.getHealthStatus().subscribe({
                        next: () => resolve(),
                        error: () => authService.logout()
                      });
                    } else {
                      resolve();
                    }
                  });
              });
            }
          });
        });
      };
    },
    deps: [
      LanguageStore,
      AuthService,
      IdentityStore,
      ToolsService,
      SnippetsStore,
      Sentry.TraceService
    ],
    multi: true,
  };
};
